import { doc, updateDoc, arrayUnion, arrayRemove, collection, query, where, getDocs, deleteField, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Regular Teacher Operations
export const addRequiredSubject = async (classId, subject) => {
  try {
    const classRef = doc(db, 'classes', classId);
    await updateDoc(classRef, {
      requiredSubjects: arrayUnion(subject)
    });
    return { success: true };
  } catch (error) {
    console.error('Error adding required subject:', error);
    return { success: false, error: error.message };
  }
};

export const removeRequiredSubject = async (classId, subject) => {
  try {
    const classRef = doc(db, 'classes', classId);
    await updateDoc(classRef, {
      requiredSubjects: arrayRemove(subject)
    });
    return { success: true };
  } catch (error) {
    console.error('Error removing required subject:', error);
    return { success: false, error: error.message };
  }
};

// Subject Teacher Operations
export const getClassesNeedingSubject = async (subject, schoolId = null) => {
  try {
    const constraints = [
      where('requiredSubjects', 'array-contains', subject),
      where('isActive', '==', true)
    ];
    if (schoolId) {
      constraints.push(where('schoolId', '==', schoolId));
    }
    const classesQuery = query(collection(db, 'classes'), ...constraints);
    const snapshot = await getDocs(classesQuery);
    const classes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, classes };
  } catch (error) {
    console.error('Error getting classes needing subject:', error);
    return { success: false, error: error.message, classes: [] };
  }
};

export const assignSubjectTeacher = async (teacherId, classId, subject) => {
  try {
    // First, fetch the class to validate
    const classRef = doc(db, 'classes', classId);
    const classDoc = await getDoc(classRef);
    
    if (!classDoc.exists()) {
      return { success: false, error: 'Class not found' };
    }
    
    const classData = classDoc.data();
    const requiredSubjects = classData.requiredSubjects || [];
    
    // Validate that the subject is required by the class
    if (!requiredSubjects.includes(subject)) {
      return { success: false, error: `Subject '${subject}' is not required by this class` };
    }
    
    // Fetch the teacher to validate their specialty
    const teacherRef = doc(db, 'users', teacherId);
    const teacherDoc = await getDoc(teacherRef);
    
    if (!teacherDoc.exists()) {
      return { success: false, error: 'Teacher not found' };
    }
    
    const teacherData = teacherDoc.data();
    const teacherSpecialty = teacherData.specialtySubject;
    
    if (!teacherSpecialty) {
      return { success: false, error: 'Teacher has no specialty subject' };
    }
    
    // Validate that the teacher's specialty matches the subject
    if (teacherSpecialty !== subject) {
      return { success: false, error: `Teacher's specialty '${teacherSpecialty}' does not match subject '${subject}'` };
    }
    
    // All validations passed, proceed with assignment
    await updateDoc(classRef, {
      [`subjectTeacherAssignments.${subject}`]: teacherId
    });
    return { success: true };
  } catch (error) {
    console.error('Error assigning subject teacher:', error);
    return { success: false, error: error.message };
  }
};

export const removeSubjectTeacherAssignment = async (classId, subject) => {
  try {
    const classRef = doc(db, 'classes', classId);
    await updateDoc(classRef, {
      [`subjectTeacherAssignments.${subject}`]: deleteField()
    });
    return { success: true };
  } catch (error) {
    console.error('Error removing subject teacher assignment:', error);
    return { success: false, error: error.message };
  }
};

export const getClassesForSubjectTeacher = async (teacherId, subject, schoolId = null) => {
  try {
    const constraints = [
      where(`subjectTeacherAssignments.${subject}`, '==', teacherId),
      where('isActive', '==', true)
    ];
    if (schoolId) {
      constraints.push(where('schoolId', '==', schoolId));
    }
    const classesQuery = query(collection(db, 'classes'), ...constraints);
    const snapshot = await getDocs(classesQuery);
    const classes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, classes };
  } catch (error) {
    console.error('Error getting classes for subject teacher:', error);
    return { success: false, error: error.message, classes: [] };
  }
};

export const getSubjectTeachersForClass = async (classId) => {
  try {
    const classDoc = await getDocs(query(collection(db, 'classes')));
    const classData = classDoc.docs.find(doc => doc.id === classId);
    if (!classData) {
      return { success: false, error: 'Class not found', subjectTeachers: [] };
    }

    const assignments = classData.data().subjectTeacherAssignments || {};
    const subjectTeachers = Object.entries(assignments).map(([subject, teacherId]) => ({
      subject,
      teacherId
    }));

    return { success: true, subjectTeachers };
  } catch (error) {
    console.error('Error getting subject teachers for class:', error);
    return { success: false, error: error.message, subjectTeachers: [] };
  }
};

export const getAllTeachersForClass = async (classId) => {
  try {
    const classDoc = await getDocs(query(collection(db, 'classes')));
    const classData = classDoc.docs.find(doc => doc.id === classId);
    if (!classData) {
      return { success: false, error: 'Class not found', regularTeacherId: null, subjectTeachers: [] };
    }

    const data = classData.data();
    const regularTeacherId = data.teacherId || null;
    const assignments = data.subjectTeacherAssignments || {};
    const subjectTeachers = Object.entries(assignments).map(([subject, teacherId]) => ({
      subject,
      teacherId
    }));

    return { success: true, regularTeacherId, subjectTeachers };
  } catch (error) {
    console.error('Error getting all teachers for class:', error);
    return { success: false, error: error.message, regularTeacherId: null, subjectTeachers: [] };
  }
};

export const getSubjectTeachersForSubject = async (subject, schoolId = null) => {
  try {
    const constraints = [
      where('role', '==', 'teacher'),
      where('teacherType', '==', 'subject'),
      where('specialtySubjects', 'array-contains', subject)
    ];
    if (schoolId) {
      constraints.push(where('schoolId', '==', schoolId));
    }
    const teachersQuery = query(collection(db, 'users'), ...constraints);
    const snapshot = await getDocs(teachersQuery);
    const teachers = snapshot.docs.map(doc => ({
      id: doc.id,
      name: doc.data().username,
      specialtySubject: subject
    }));
    return { success: true, teachers };
  } catch (error) {
    console.error('Error getting subject teachers for subject:', error);
    return { success: false, error: error.message, teachers: [] };
  }
};
